"use client";

import { motion } from "framer-motion";
import { ArrowRight, CheckCircle, Mail } from "lucide-react";
import Link from "next/link";
import type { SubmitEvent } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { siteConfig } from "@/lib/seo/config";
import { useSubscriptionStore } from "@/lib/store/subscription-store";

const footerLinks = {
	shop: [
		{ label: "Build-A-Box", href: "/build-a-box" },
		{ label: "Our Meals", href: "/#meals" },
		{ label: "Blog", href: "/blog" },
		//{ label: "IAM X FBB", href: "/fbb" },
		// Disabled: page removed
		// { label: "Swag", href: "/swag" },
		// Disabled: page removed
		// { label: "Spices", href: "/spices" },
		{ label: "Gift Cards", href: "/gift-cards" },
	],
	company: [
		{ label: "About Us", href: "/about" },
		{ label: "Ice Age Culinary", href: "https://www.iceageculinary.com" },
		{ label: "Cooking Lessons", href: "https://www.iceageculinary.com" },
		{
			label: "The Ice Age Meltdown",
			href: "https://www.iceageculinary.com/ice-age-meltdown-landing-page",
		},
	],
	support: [
		{ label: "FAQ", href: "/#faq" },
		{ label: "Reviews", href: "/#reviews" },
		{ label: "Contact", href: "/contact" },
	],
	community: [
		{ label: "Keala Foundation", href: "https://www.kealafoundation.com/" },
	],
};

export function Footer() {
	const email = useSubscriptionStore((s) => s.email);
	const subscribed = useSubscriptionStore((s) => s.isSubscribed);
	const alreadySubscribed = useSubscriptionStore((s) => s.alreadySubscribed);
	const isSubmitting = useSubscriptionStore((s) => s.isSubmitting);
	const error = useSubscriptionStore((s) => s.error);
	const setEmail = useSubscriptionStore((s) => s.setEmail);
	const subscribe = useSubscriptionStore((s) => s.subscribe);

	const handleSubscribe = async (e: SubmitEvent<HTMLFormElement>) => {
		e.preventDefault();
		await subscribe();
	};

	return (
		<footer className="border-t border-border bg-card">
			<div className="bg-primary/5 border-b border-border">
				<div className="container mx-auto px-4 py-8 sm:py-10">
					<div className="max-w-xl mx-auto text-center space-y-4">
						<div className="flex items-center justify-center gap-2">
							<Mail className="h-5 w-5 text-primary" />
							<h3 className="font-bold text-lg">Stay in the Loop</h3>
						</div>
						<p className="text-sm text-muted-foreground">
							Get exclusive deals, new meal drops, and nutrition tips delivered
							straight to your inbox.
						</p>

						{subscribed ? (
							<div className="flex items-center justify-center gap-2 text-emerald-600 dark:text-emerald-400">
								<CheckCircle className="h-5 w-5" />
								<span className="font-medium text-sm">
									{alreadySubscribed
										? "You're already on our subscription list."
										: "You're subscribed! Check your inbox for a welcome gift."}
								</span>
							</div>
						) : (
							<form
								onSubmit={handleSubscribe}
								toolname="subscribe-newsletter"
								tooldescription="Subscribe an email address to the Ice Age Meals newsletter for deals and new meal drops. The shopper reviews and submits."
								className="flex flex-col sm:flex-row gap-2 max-w-md mx-auto w-full"
							>
								<Input
									type="email"
									name="email"
									id="footer-newsletter-email"
									autoComplete="email"
									aria-label="Email address"
									toolparamdescription="Email address to subscribe."
									value={email}
									onChange={(e) => setEmail(e.target.value)}
									placeholder="Enter your email"
									required
									className="flex-1 w-full h-12 sm:h-10 text-base sm:text-sm bg-background border-border focus-visible:ring-primary placeholder:text-muted-foreground"
								/>
								<Button
									type="submit"
									disabled={isSubmitting || !email.trim()}
									className="h-12 sm:h-10 px-5 text-base sm:text-sm font-semibold w-full sm:w-auto shrink-0 transition-all duration-300 relative group"
								>
									<span
										className={`block font-semibold transition-transform duration-300 ${isSubmitting ? "-translate-y-8 opacity-0" : "translate-y-0 opacity-100 flex items-center justify-center"}`}
									>
										Subscribe
										<ArrowRight className="ml-1.5 h-4 w-4 sm:h-3.5 sm:w-3.5 group-hover:translate-x-1 transition-transform" />
									</span>
									<span
										className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${
											isSubmitting ? "opacity-100" : "opacity-0"
										}`}
									>
										<svg
											className="animate-spin h-5 w-5 text-primary-foreground"
											xmlns="http://www.w3.org/2000/svg"
											fill="none"
											viewBox="0 0 24 24"
											aria-hidden="true"
										>
											<title>Loading...</title>
											<circle
												className="opacity-25"
												cx="12"
												cy="12"
												r="10"
												stroke="currentColor"
												strokeWidth="4"
											></circle>
											<path
												className="opacity-75"
												fill="currentColor"
												d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
											></path>
										</svg>
									</span>
								</Button>
							</form>
						)}

						{/* Error Message Inline */}
						{error && (
							<motion.div
								initial={{ opacity: 0, y: -5 }}
								animate={{ opacity: 1, y: 0 }}
								className="text-xs text-red-500 dark:text-red-400 font-medium max-w-sm mx-auto text-center"
							>
								{error}
							</motion.div>
						)}

						<p className="text-[10px] text-muted-foreground">
							No spam, ever.{" "}
							<Link
								href="/preferences"
								className="underline underline-offset-2 hover:text-foreground transition-colors"
							>
								Manage preferences.
							</Link>
						</p>
					</div>
				</div>
			</div>

			<div className="container mx-auto px-4 py-12">
				<div className="grid grid-cols-2 gap-8 md:grid-cols-5">
					<div className="col-span-2 md:col-span-1">
						<Link href="/" className="inline-block">
							<span className="text-xl font-bold tracking-tight">
								Ice Age Meals
							</span>
						</Link>
						<p className="mt-4 text-sm text-muted-foreground">
							Fuel The Big Life. Healthy, Paleo-friendly frozen meals delivered
							to your door.
						</p>
						<div className="mt-6 text-sm text-muted-foreground space-y-1">
							<p>{siteConfig.business.address.streetAddress}</p>
							<p>
								{siteConfig.business.address.addressLocality},{" "}
								{siteConfig.business.address.addressRegion}{" "}
								{siteConfig.business.address.postalCode}
							</p>
							<p className="pt-2">
								<a
									href={`mailto:${siteConfig.business.email}`}
									className="hover:text-foreground transition-colors"
								>
									{siteConfig.business.email}
								</a>
							</p>
						</div>
					</div>

					<div>
						<h3 className="font-semibold text-sm uppercase tracking-wider">
							Shop
						</h3>
						<ul className="mt-4 space-y-2">
							{footerLinks.shop.map((link) => (
								<li key={link.label}>
									<Link
										href={link.href}
										className="text-sm text-muted-foreground transition-colors hover:text-foreground"
										target={link.href.startsWith("http") ? "_blank" : undefined}
									>
										{link.label}
									</Link>
								</li>
							))}
						</ul>
					</div>

					<div>
						<h3 className="font-semibold text-sm uppercase tracking-wider">
							Company
						</h3>
						<ul className="mt-4 space-y-2">
							{footerLinks.company.map((link) => (
								<li key={link.label}>
									<Link
										href={link.href}
										className="text-sm text-muted-foreground transition-colors hover:text-foreground"
										target={link.href.startsWith("http") ? "_blank" : undefined}
									>
										{link.label}
									</Link>
								</li>
							))}
						</ul>
					</div>

					<div>
						<h3 className="font-semibold text-sm uppercase tracking-wider">
							Support
						</h3>
						<ul className="mt-4 space-y-2">
							{footerLinks.support.map((link) => (
								<li key={link.label}>
									<Link
										href={link.href}
										className="text-sm text-muted-foreground transition-colors hover:text-foreground"
									>
										{link.label}
									</Link>
								</li>
							))}
						</ul>
					</div>

					<div>
						<h3 className="font-semibold text-sm uppercase tracking-wider">
							Grind Then Give
						</h3>
						<ul className="mt-4 space-y-2">
							{footerLinks.community.map((link) => (
								<li key={link.label}>
									<Link
										href={link.href}
										className="text-sm text-muted-foreground transition-colors hover:text-foreground"
										target={link.href.startsWith("http") ? "_blank" : undefined}
									>
										{link.label}
									</Link>
								</li>
							))}
						</ul>
					</div>
				</div>

				<div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-border pt-8 md:flex-row">
					<p className="text-sm text-muted-foreground">
						&copy; {new Date().getFullYear()} Ice Age Meals. All rights
						reserved.
					</p>

					<div className="flex items-center gap-4">
						<Link
							href={siteConfig.business.social.facebook}
							target="_blank"
							rel="noopener noreferrer"
							className="text-muted-foreground hover:text-[#1877F2] transition-colors"
							aria-label="Facebook"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 24 24"
								fill="currentColor"
								aria-hidden="true"
								className="h-5 w-5"
							>
								<path d="M22 12A10 10 0 1 0 10.44 21.88v-6.98H7.9V12h2.54V9.8c0-2.5 1.49-3.88 3.77-3.88 1.09 0 2.23.2 2.23.2v2.46h-1.26c-1.24 0-1.62.77-1.62 1.56V12h2.77l-.44 2.9h-2.33v6.98A10 10 0 0 0 22 12" />
							</svg>
						</Link>
						<Link
							href={siteConfig.business.social.instagram}
							target="_blank"
							rel="noopener noreferrer"
							className="text-muted-foreground hover:text-[#E4405F] transition-colors"
							aria-label="Instagram"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 24 24"
								fill="currentColor"
								aria-hidden="true"
								className="h-5 w-5"
							>
								<path d="M7.75 2h8.5A5.75 5.75 0 0 1 22 7.75v8.5A5.75 5.75 0 0 1 16.25 22h-8.5A5.75 5.75 0 0 1 2 16.25v-8.5A5.75 5.75 0 0 1 7.75 2m0 1.8a3.95 3.95 0 0 0-3.95 3.95v8.5a3.95 3.95 0 0 0 3.95 3.95h8.5a3.95 3.95 0 0 0 3.95-3.95v-8.5a3.95 3.95 0 0 0-3.95-3.95zM18 6.2a1.1 1.1 0 1 0 0 2.2 1.1 1.1 0 0 0 0-2.2M12 7a5 5 0 1 1 0 10 5 5 0 0 1 0-10m0 1.8A3.2 3.2 0 1 0 12 15.2 3.2 3.2 0 0 0 12 8.8" />
							</svg>
						</Link>
						<Link
							href={siteConfig.business.social.youtube}
							target="_blank"
							rel="noopener noreferrer"
							className="text-muted-foreground hover:text-[#FF0000] transition-colors"
							aria-label="YouTube"
						>
							<svg
								xmlns="http://www.w3.org/2000/svg"
								viewBox="0 0 24 24"
								fill="currentColor"
								aria-hidden="true"
								className="h-5 w-5"
							>
								<path d="M23 12s0-3.02-.38-4.48a3.2 3.2 0 0 0-2.25-2.25C18.9 4.9 12 4.9 12 4.9s-6.9 0-8.37.37A3.2 3.2 0 0 0 1.38 7.5C1 8.98 1 12 1 12s0 3.02.38 4.48a3.2 3.2 0 0 0 2.25 2.25C5.1 19.1 12 19.1 12 19.1s6.9 0 8.37-.37a3.2 3.2 0 0 0 2.25-2.25C23 15.02 23 12 23 12M9.2 15.7V8.3L15.7 12z" />
							</svg>
						</Link>
					</div>

					<div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
						<Link
							href="/privacy-policy"
							className="text-sm text-muted-foreground transition-colors hover:text-foreground"
						>
							Privacy Policy
						</Link>
						<Link
							href="/terms-of-service"
							className="text-sm text-muted-foreground transition-colors hover:text-foreground"
						>
							Terms of Service
						</Link>
						<Link
							href="/shipping-policy"
							className="text-sm text-muted-foreground transition-colors hover:text-foreground"
						>
							Shipping Policy
						</Link>
						<Link
							href="/refund-policy"
							className="text-sm text-muted-foreground transition-colors hover:text-foreground"
						>
							Refund Policy
						</Link>
						<Link
							href="/cookie-policy"
							className="text-sm text-muted-foreground transition-colors hover:text-foreground"
						>
							Cookie Policy
						</Link>
					</div>
				</div>
			</div>
		</footer>
	);
}
